/**
 * Catalog — single source of truth for all category & product pages.
 *
 * Structure:
 *   catalog[lang][category] = {
 *     id, name, eyebrow, intro, heroImage, ctaLabel,
 *     variants: [{
 *       slug, name, badge, tagline, price, image, summary,
 *       features: [..], specs: { Material, Gaas, Frame, … }
 *     }]
 *   }
 *
 * Lookup helpers exported at the bottom.
 */
import { media } from './i18n';

const HM = '/products/';

// Common spec rows reused across most window/door products
const COMMON_SPECS = {
  nl: {
    materiaal: 'Aluminium frame',
    afwerking: 'Pulvercoating RAL-kleuren',
    gaas: 'Fiberglas standaard · pollen · huisdier',
    montage: 'Klemvast — zonder boren',
    levertijd: 'Ca. 15 werkdagen',
    garantie: '5 jaar op constructie',
  },
  de: {
    material: 'Aluminiumrahmen',
    oberfläche: 'Pulverbeschichtung RAL-Farben',
    gewebe: 'Fiberglas Standard · Pollen · Haustier',
    montage: 'Klemmend — ohne Bohren',
    lieferzeit: 'Ca. 15 Werktage',
    garantie: '5 Jahre auf Konstruktion',
  },
};

// ============================================================
// NL
// ============================================================
const NL = {
  ramen: {
    id: 'ramen',
    name: 'Horren voor ramen',
    eyebrow: 'Collectie · Ramen',
    intro:
      'Open ramen, frisse lucht, geen muggen. Onze raamhorren passen in elk kozijn — van standaard tot dakraam. Maatwerk, zonder boren waar mogelijk.',
    heroImage: HM + 'horrenmaker-spannrahmen.webp',
    ctaLabel: 'Vraag een gratis offerte',
    variants: [
      {
        slug: 'spannrahmen',
        name: 'Spannrahmen',
        badge: 'Bestseller',
        tagline: 'De onzichtbare klassieker — klemt vast, blijft staan.',
        image: HM + 'horrenmaker-spannrahmen.webp',
        summary:
          'Vast frame dat strak in het kozijn klemt. Onzichtbaar van binnen. Perfect voor slaapkamer- en woonkamerramen waar u dagelijks niet doorheen hoeft.',
        features: [
          'Klemvast — zonder boren mogelijk',
          'Vlak met het kozijn — bijna onzichtbaar',
          'In één beweging uit het raam te tillen',
          'Aluminium frame, RAL-kleur op aanvraag',
        ],
      },
      {
        slug: 'drehfenster',
        name: 'Draairaam-hor',
        badge: 'Voor dagelijks gebruik',
        tagline: 'Opent als een raam — perfect voor planten op de vensterbank.',
        image: HM + 'horrenmaker-drehfenster.webp',
        summary:
          'Hor met scharnier — open als een raam, sluit met magneet. Ideaal voor keuken- en balkonramen die u dagelijks bedient.',
        features: [
          'Scharnier-systeem, sluit met magneet',
          'Robust aluminium profiel',
          'Geschikt voor zware dagelijkse belasting',
          'Geen demontage nodig om door het raam te gaan',
        ],
      },
      {
        slug: 'plissee-fenster',
        name: 'Plissé-hor voor ramen',
        badge: 'Compact',
        tagline: 'Vouwt op als een accordeon — alleen zichtbaar als u hem nodig hebt.',
        image: HM + 'horrenmaker-plissee-fenster.webp',
        summary:
          'Vouwhor die zich opvouwt naar één zijde. Compact opgeborgen in een smalle behuizing aan de zijkant.',
        features: [
          'Vouwt volledig weg — onzichtbaar wanneer open',
          'Geleider boven en onder',
          'Op maat tot 2,5 m breed',
          'Geschikt voor brede ramen en schuiframen',
        ],
      },
      {
        slug: 'rollo-fenster',
        name: 'Rolhor voor ramen',
        badge: 'Praktisch',
        tagline: 'Rolt op naar boven — gebruiksvriendelijk en discreet.',
        image: HM + 'horrenmaker-rollo-fenster.webp',
        summary:
          'Insectenhor op rol, opgeborgen in een aluminium cassette boven het raam. Met één hand uit te trekken en vast te zetten.',
        features: [
          'Veermechanisme met soft-close',
          'Aluminium cassette boven het raam',
          'Geluidloos rollen',
          'Optioneel met rem in elke positie',
        ],
      },
      {
        slug: 'dachfenster',
        name: 'Hor voor dakramen',
        badge: 'Velux & Roto',
        tagline: 'Speciaal voor dakramen — Velux, Roto, Fakro en meer.',
        image: HM + 'horrenmaker-dachfenster-rollo.webp',
        summary:
          'Rolhor met geleider voor schuine dakramen. Compatibel met alle bekende merken. Wordt aan de bovenzijde van het dakraam bevestigd.',
        features: [
          'Voor Velux, Roto, Fakro en gelijkwaardig',
          'Past op draaikiep- en tuimelramen',
          'UV-bestendig gaas',
          'Eenvoudig zelf te bedienen',
        ],
      },
    ],
  },

  deuren: {
    id: 'deuren',
    name: 'Horren voor deuren',
    eyebrow: 'Collectie · Deuren',
    intro:
      'Tuindeur, balkondeur, schuifdeur — wij maken de hor erbij. Loop ze open, vergeet ze dicht. Robuust aluminium, jaren mee.',
    heroImage: HM + 'horrenmaker-schiebetuer.webp',
    ctaLabel: 'Vraag advies aan',
    variants: [
      {
        slug: 'drehtuer',
        name: 'Draaihor',
        badge: 'Bestseller',
        tagline: 'Eén deur, één hor — opent en sluit als een echte deur.',
        image: HM + 'horrenmaker-drehtuer.webp',
        summary:
          'Aluminium draaihor met scharnier en magneetsluiting. Sluit vanzelf, blijft niet open staan. Onze meest verkochte deurhor.',
        features: [
          'Scharnier-deur, opent links of rechts',
          'Magnetische zelfsluiting',
          'Aluminium profiel pulvercoating',
          'Optioneel met onderdrempel of zonder',
        ],
      },
      {
        slug: 'pendeltuer',
        name: 'Pendelhor',
        badge: 'Hands-free',
        tagline: 'Loop er gewoon doorheen — sluit zichzelf achter u.',
        image: HM + 'horrenmaker-pendeltuer.webp',
        summary:
          'Tweezijdig zwaaiende deur die u met heup of voet kunt openen. Ideaal als u met handen vol naar buiten loopt.',
        features: [
          'Opent beide kanten op (push-through)',
          'Veermechanisme sluit vanzelf',
          'Hand-vrij — handig in de keuken',
          'Robuust voor hoge frequentie gebruik',
        ],
      },
      {
        slug: 'schiebetuer',
        name: 'Schuifdeur-hor',
        badge: 'Voor brede openingen',
        tagline: 'Loopt op een rail — voor schuifpuien en brede openingen.',
        image: HM + 'horrenmaker-schiebetuer.webp',
        summary:
          'Hor die op een onder- en bovenrail beweegt. Perfect voor brede tuindeuren en schuifpuien tot 3+ meter.',
        features: [
          'Onder- en bovenrail',
          'Soft-close eindstop',
          'Op maat tot 3 m breed',
          'Optioneel dubbel uitgevoerd (van twee kanten)',
        ],
      },
      {
        slug: 'plisseetuer',
        name: 'Plissé-deurhor',
        badge: 'Compact',
        tagline: 'Vouwt volledig weg in een smalle zijkant.',
        image: HM + 'horrenmaker-plisseetuer.webp',
        summary:
          'Vouwhor voor deuren — vouwt accordeonsgewijs op in een smalle koker. Schoon, modern, geen rommelig zicht.',
        features: [
          'Volledig op te vouwen, onzichtbaar in stand-by',
          'Geleider op vloer en plafond',
          'Op maat tot 2,5 m breed',
          'Geschikt voor wisselend gebruik',
        ],
      },
    ],
  },

  lichtschachten: {
    id: 'lichtschachten',
    name: 'Lichtschacht-afdekking',
    eyebrow: 'Collectie · Lichtschachten',
    intro:
      'Geen blad, geen vuil, geen ongewenste gasten meer in uw kelder. Stevige afdekking op maat voor elke lichtschacht.',
    heroImage: HM + 'horrenmaker-lichtschachtabdeckung.webp',
    ctaLabel: 'Vraag op maat aan',
    variants: [
      {
        slug: 'standaard',
        name: 'Lichtschacht-afdekking op maat',
        badge: 'Maatwerk',
        tagline: 'Op maat gemaakt voor uw lichtschacht.',
        image: HM + 'horrenmaker-lichtschachtabdeckung.webp',
        summary:
          'Aluminium frame met duurzaam gaas of roostermat. Houdt blad, vuil en knaagdieren tegen, laat licht en regenwater door.',
        features: [
          'Aluminium frame op maat',
          'Optie: fijn gaas of begaanbare rooster',
          'Lichtdoorlatend',
          'Klemvast of geschroefd verkrijgbaar',
        ],
      },
    ],
  },
};

// ============================================================
// DE
// ============================================================
const DE = {
  ramen: {
    id: 'ramen',
    name: 'Insektenschutz für Fenster',
    eyebrow: 'Kollektion · Fenster',
    intro:
      'Offene Fenster, frische Luft, keine Mücken. Unser Fenster-Insektenschutz passt in jeden Rahmen — vom Standard- bis zum Dachfenster. Maßarbeit, wo möglich ohne Bohren.',
    heroImage: HM + 'horrenmaker-spannrahmen.webp',
    ctaLabel: 'Kostenloses Angebot anfragen',
    variants: [
      {
        slug: 'spannrahmen',
        name: 'Spannrahmen',
        badge: 'Bestseller',
        tagline: 'Der unsichtbare Klassiker — fest geklemmt, jahrelang vergessen.',
        image: HM + 'horrenmaker-spannrahmen.webp',
        summary:
          'Fester Rahmen, der sich straff in den Fensterrahmen klemmt. Von innen unsichtbar. Perfekt für Schlaf- und Wohnzimmerfenster.',
        features: [
          'Klemmend — ohne Bohren möglich',
          'Bündig mit dem Rahmen — fast unsichtbar',
          'In einem Griff zum Reinigen entnehmbar',
          'Aluminiumrahmen, RAL-Farbe auf Anfrage',
        ],
      },
      {
        slug: 'drehfenster',
        name: 'Drehfenster-Insektenschutz',
        badge: 'Für täglichen Gebrauch',
        tagline: 'Öffnet wie ein Fenster — perfekt für Pflanzen auf der Fensterbank.',
        image: HM + 'horrenmaker-drehfenster.webp',
        summary:
          'Insektenschutz mit Scharnier — öffnet wie ein Fenster, schließt mit Magnet. Ideal für Küchen- und Balkonfenster.',
        features: [
          'Scharnier-System, Magnetverschluss',
          'Robustes Aluminiumprofil',
          'Für tägliche Beanspruchung gemacht',
          'Keine Demontage zum Durchgreifen',
        ],
      },
      {
        slug: 'plissee-fenster',
        name: 'Plissee-Fenster',
        badge: 'Kompakt',
        tagline: 'Faltet sich wie ein Akkordeon — nur sichtbar wenn benötigt.',
        image: HM + 'horrenmaker-plissee-fenster.webp',
        summary:
          'Faltbarer Insektenschutz, der sich seitlich vollständig wegfaltet. Kompakt in einer schmalen Aluminium-Kassette verstaut.',
        features: [
          'Faltet vollständig weg — unsichtbar im Ruhezustand',
          'Führung oben und unten',
          'Maßanfertigung bis 2,5 m Breite',
          'Geeignet für breite Fenster',
        ],
      },
      {
        slug: 'rollo-fenster',
        name: 'Rollo-Insektenschutz',
        badge: 'Praktisch',
        tagline: 'Rollt nach oben weg — bedienerfreundlich und unauffällig.',
        image: HM + 'horrenmaker-rollo-fenster.webp',
        summary:
          'Insektenschutz auf Rolle, untergebracht in einer Aluminium-Kassette über dem Fenster. Mit einer Hand herauszuziehen.',
        features: [
          'Federmechanismus mit Soft-close',
          'Aluminium-Kassette über dem Fenster',
          'Lautloser Rolllauf',
          'Optional mit Stopp in jeder Position',
        ],
      },
      {
        slug: 'dachfenster',
        name: 'Dachfenster-Insektenschutz',
        badge: 'Velux & Roto',
        tagline: 'Speziell für Dachfenster — Velux, Roto, Fakro & mehr.',
        image: HM + 'horrenmaker-dachfenster-rollo.webp',
        summary:
          'Rollo-Insektenschutz mit Führung für Schrägdachfenster. Kompatibel mit allen gängigen Marken.',
        features: [
          'Für Velux, Roto, Fakro und vergleichbare',
          'Passt auf Schwing- und Klappschwingfenster',
          'UV-beständiges Gewebe',
          'Einfache Eigenbedienung',
        ],
      },
    ],
  },

  deuren: {
    id: 'deuren',
    name: 'Insektenschutz für Türen',
    eyebrow: 'Kollektion · Türen',
    intro:
      'Gartentür, Balkontür, Schiebetür — wir bauen den passenden Insektenschutz dazu. Geh hindurch, vergiss, dass er da ist.',
    heroImage: HM + 'horrenmaker-schiebetuer.webp',
    ctaLabel: 'Beratung anfragen',
    variants: [
      {
        slug: 'drehtuer',
        name: 'Drehtür',
        badge: 'Bestseller',
        tagline: 'Eine Tür, ein Insektenschutz — öffnet und schließt wie eine richtige Tür.',
        image: HM + 'horrenmaker-drehtuer.webp',
        summary:
          'Aluminium-Drehtür mit Scharnier und Magnetverschluss. Schließt von selbst, bleibt nicht offen. Unser meistverkauftes Modell.',
        features: [
          'Scharnier-Tür, links oder rechts öffnend',
          'Magnetische Selbstschließung',
          'Aluminium-Profil pulverbeschichtet',
          'Optional mit oder ohne Bodenschiene',
        ],
      },
      {
        slug: 'pendeltuer',
        name: 'Pendeltür',
        badge: 'Hands-free',
        tagline: 'Geh einfach durch — sie schließt sich hinter dir.',
        image: HM + 'horrenmaker-pendeltuer.webp',
        summary:
          'Beidseitig schwingende Tür, die mit Hüfte oder Fuß zu öffnen ist. Ideal wenn du mit vollen Händen nach draußen gehst.',
        features: [
          'Beide Richtungen öffnend (Push-through)',
          'Federmechanismus schließt selbst',
          'Hands-free — praktisch in der Küche',
          'Robust für hohe Frequenz',
        ],
      },
      {
        slug: 'schiebetuer',
        name: 'Schiebetür',
        badge: 'Für breite Öffnungen',
        tagline: 'Läuft auf einer Schiene — für Schiebetüren und breite Öffnungen.',
        image: HM + 'horrenmaker-schiebetuer.webp',
        summary:
          'Insektenschutz auf Boden- und Deckenschiene. Perfekt für breite Gartentüren und Schiebetüren bis 3+ Meter.',
        features: [
          'Boden- und Deckenschiene',
          'Soft-Close Endanschlag',
          'Maßanfertigung bis 3 m Breite',
          'Optional zweiseitig ausgeführt',
        ],
      },
      {
        slug: 'plisseetuer',
        name: 'Plissee-Tür',
        badge: 'Kompakt',
        tagline: 'Faltet sich komplett in einer schmalen Seite weg.',
        image: HM + 'horrenmaker-plisseetuer.webp',
        summary:
          'Falt-Insektenschutz für Türen — akkordeonartig in einer schmalen Säule verstaut. Sauber, modern, kein Sichtchaos.',
        features: [
          'Vollständig faltbar, im Ruhezustand unsichtbar',
          'Führung auf Boden und Decke',
          'Maßanfertigung bis 2,5 m Breite',
          'Geeignet für wechselnden Gebrauch',
        ],
      },
    ],
  },

  lichtschachten: {
    id: 'lichtschachten',
    name: 'Lichtschacht-Abdeckung',
    eyebrow: 'Kollektion · Lichtschächte',
    intro:
      'Kein Laub, kein Schmutz, keine ungebetenen Gäste im Keller. Stabile Abdeckung nach Maß für jeden Lichtschacht.',
    heroImage: HM + 'horrenmaker-lichtschachtabdeckung.webp',
    ctaLabel: 'Nach Maß anfragen',
    variants: [
      {
        slug: 'standaard',
        name: 'Lichtschacht-Abdeckung nach Maß',
        badge: 'Maßarbeit',
        tagline: 'Maßgefertigt für deinen Lichtschacht.',
        image: HM + 'horrenmaker-lichtschachtabdeckung.webp',
        summary:
          'Aluminium-Rahmen mit langlebigem Gewebe oder Trittrost. Hält Laub, Schmutz und Nagetiere fern — lässt Licht und Regen durch.',
        features: [
          'Aluminium-Rahmen nach Maß',
          'Option: feines Gewebe oder begehbarer Rost',
          'Lichtdurchlässig',
          'Klemmend oder verschraubt',
        ],
      },
    ],
  },
};

const CATALOG = { nl: NL, de: DE };

export const CATEGORIES = ['ramen', 'deuren', 'lichtschachten'];

export function getCategory(lang, slug) {
  const data = CATALOG[lang] || CATALOG.nl;
  return data[slug];
}

export function getProduct(lang, categorySlug, productSlug) {
  const cat = getCategory(lang, categorySlug);
  if (!cat) return null;
  const product = cat.variants.find((v) => v.slug === productSlug);
  if (!product) return null;
  return { ...product, category: cat, specs: { ...COMMON_SPECS[lang] } };
}

export function getAllCategories(lang) {
  return CATEGORIES.map((slug) => getCategory(lang, slug)).filter(Boolean);
}

export { media };
